/**
 * 任务状态轮询器
 */
import type { Ref } from 'vue';
import type { MaterialFormulationApi } from '@/domains/material/apis/formulation-api';
import { TASK_STATUS } from '../types';

export interface TaskPollerOptions {
  /** 轮询间隔（毫秒） */
  intervalMs?: number;
  /** 当前会话 ID ref */
  sessionId: Ref<string>;
  /** 任务状态 ref */
  taskStatus: Ref<string>;
  /** pipeline 运行状态 ref */
  pipelineRunning: Ref<boolean>;
  /** API 服务 */
  api: MaterialFormulationApi;
}

export interface TaskPoller {
  /** 开始轮询 */
  start: (targetSessionId: string) => void;
  /** 停止轮询 */
  stop: () => void;
}

/**
 * 创建任务状态轮询器
 */
export function createTaskPoller(options: TaskPollerOptions): TaskPoller {
  const { intervalMs = 2000, sessionId, taskStatus, pipelineRunning, api } = options;

  let pollTimerId: ReturnType<typeof setTimeout> | null = null;
  let isPollingActive = false;
  let currentTargetSessionId = '';

  const poll = async () => {
    if (!isPollingActive || sessionId.value !== currentTargetSessionId) {
      return;
    }

    try {
      const userTask = await api.getUserTask(currentTargetSessionId);
      if (sessionId.value === currentTargetSessionId) {
        taskStatus.value = userTask.status ?? '';
        // 当 task 状态不是 running/started 时，设置 pipelineRunning 为 false
        if (
          userTask.status !== TASK_STATUS.RUNNING &&
          userTask.status !== TASK_STATUS.STARTED
        ) {
          pipelineRunning.value = false;
        }
      }
    } catch {
      // ignore polling errors
    }

    if (isPollingActive) {
      pollTimerId = setTimeout(poll, intervalMs);
    }
  };

  const start = (targetSessionId: string) => {
    currentTargetSessionId = targetSessionId;
    isPollingActive = true;
    pollTimerId = setTimeout(poll, intervalMs);
  };

  const stop = () => {
    isPollingActive = false;
    if (pollTimerId) {
      clearTimeout(pollTimerId);
      pollTimerId = null;
    }
  };

  return { start, stop };
}
